import DefaultImage, { type ImageOptions } from '@tiptap/extension-image';
import { ResizableNodeView } from '@tiptap/core';
import type { UploadDelegate, UploadResult } from './upload';

export interface ImageUploadOptions extends ImageOptions {
  uploadFile: ((file: File, delegate: UploadDelegate) => Promise<UploadResult>) | null;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    imageUpload: {
      startImageUpload: (file: File) => ReturnType;
    };
  }
}

const Image = DefaultImage.extend<ImageUploadOptions>({
  // Block-level image: each image lives on its own line and may carry an
  // editable <figcaption> driven by `alt`.
  inline: false,

  atom: true,

  group: 'block',

  addOptions() {
    return {
      ...this.parent?.(),
      uploadFile: null,
    };
  },

  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
      },
      height: {
        default: null,
      },
      file: {
        default: null,
        rendered: false,
      },
    };
  },

  addStorage() {
    return {
      markdown: {
        serialize(state: any, node: any) {
          const src = node.attrs.src || '';
          const alt = node.attrs.alt || '';
          const title = node.attrs.title || '';
          const width = node.attrs.width;
          const height = node.attrs.height;

          // When the image has a stored width/height (or a title), markdown's
          // `![alt](src)` syntax can't express it — fall back to a raw <img>
          // tag so the dimensions round-trip through markdown -> HTML.
          if (width || height || title) {
            const escAttr = (v: string) => String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
            const parts = [`src="${escAttr(src)}"`];
            if (alt) parts.push(`alt="${escAttr(alt)}"`);
            if (title) parts.push(`title="${escAttr(title)}"`);
            if (width) parts.push(`width="${Math.round(Number(width))}"`);
            if (height) parts.push(`height="${Math.round(Number(height))}"`);
            state.write(`<img ${parts.join(' ')}>`);
          } else {
            const escAlt = alt.replace(/[\[\]]/g, '\\$&');
            state.write(`![${escAlt}](${src})`);
          }
          state.closeBlock(node);
        },
        parse: {
          setup() {
            // markdown-it produces standard <img>; nothing to register.
          },
          // Unwrap `<p><img></p>` so a block-level image can match the schema.
          updateDOM(element: HTMLElement) {
            element.querySelectorAll('p > img').forEach((img) => {
              const p = img.parentElement;
              if (!p || p.tagName !== 'P') return;
              const onlyImage = Array.from(p.childNodes).every(
                (n) =>
                  n === img || (n.nodeType === Node.TEXT_NODE && !(n.textContent || '').trim()),
              );
              if (onlyImage) p.replaceWith(img);
            });
          },
        },
      },
    };
  },

  addCommands() {
    return {
      ...this.parent?.(),
      startImageUpload:
        (file: File) =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs: { file },
            })
            .run();
        },
    };
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const { view } = editor;

      const img = document.createElement('img');

      if (node.attrs.src) {
        img.src = node.attrs.src;
      } else if (node.attrs.file) {
        img.src = URL.createObjectURL(node.attrs.file);
      }
      if (node.attrs.alt) img.alt = node.attrs.alt;

      const resizable = new ResizableNodeView({
        element: img,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          // Clamp width to the editor's available width so the image (and its
          // proportional height) can never overflow the container.
          const maxWidth = (figure.parentElement as HTMLElement | null)?.clientWidth;
          if (maxWidth && width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          img.style.width = `${width}px`;
          img.style.height = `${height}px`;
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos === undefined) return;
          const maxWidth = (figure.parentElement as HTMLElement | null)?.clientWidth;
          if (maxWidth && width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          this.editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes(this.name, { width, height })
            .run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type.name !== 'image') return false;
          return true;
        },
        options: {
          directions: ['bottom-right', 'top-right'],
          min: { width: 50 },
          preserveAspectRatio: true,
        },
      });

      const figure = document.createElement('figure');
      figure.classList.add('image-figure');
      figure.appendChild(resizable.dom);

      const caption = document.createElement('figcaption');
      caption.contentEditable = 'true';
      caption.setAttribute('data-placeholder', '添加图片说明…');
      caption.textContent = node.attrs.alt || '';
      figure.appendChild(caption);

      let isSelected = false;
      const updateCaptionVisibility = () => {
        const hasAlt = !!(caption.textContent || '').trim();
        caption.style.display = hasAlt || isSelected ? '' : 'none';
        if (!hasAlt) caption.classList.add('is-empty');
        else caption.classList.remove('is-empty');
      };
      updateCaptionVisibility();

      caption.addEventListener('input', () => {
        const pos = getPos();
        if (pos === undefined) return;
        const current = view.state.doc.nodeAt(pos);
        if (!current) return;
        const altText = caption.textContent || '';
        const tr = view.state.tr.setNodeMarkup(pos, undefined, {
          ...current.attrs,
          alt: altText,
        });
        // Keep caption visible while empty if the user is editing it.
        view.dispatch(tr);
        img.alt = altText;
        updateCaptionVisibility();
      });

      caption.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          caption.blur();
        }
      });

      // Don't let figcaption clicks select the image node.
      caption.addEventListener('mousedown', (event) => {
        event.stopPropagation();
      });

      // Upload progress overlay (attach to the resizable wrapper, not the figure,
      // so it sits over the image only).
      if (node.attrs.file) {
        const uploadProgress = document.createElement('div');
        uploadProgress.classList.add('image-upload-progress');
        (resizable.dom as HTMLElement).appendChild(uploadProgress);

        uploadProgress.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M18.364 5.63604L16.9497 7.05025C15.683 5.7835 13.933 5 12 5C8.13401 5 5 8.13401 5 12C5 15.866 8.13401 19 12 19C15.866 19 19 15.866 19 12H21C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C14.4853 3 16.7353 4.00736 18.364 5.63604Z"></path></svg>
          <span>Uploading</span>
          <span class="image-upload-progress-content">0%</span>
        `;
        const progressContent = uploadProgress.querySelector('.image-upload-progress-content');

        this.options
          .uploadFile(node.attrs.file, {
            directUploadWillStoreFileWithXHR: (xhr: XMLHttpRequest) => {
              xhr.upload.addEventListener('progress', (event: ProgressEvent) => {
                const progress = Math.round((event.loaded / event.total) * 100);
                progressContent!.textContent = `${progress}%`;
              });
            },
          })
          .then((json: { url: string }) => {
            const { height, width } = img.getBoundingClientRect();
            const pos = getPos();
            if (pos === undefined) return;
            const current = view.state.doc.nodeAt(pos);
            view.dispatch(
              view.state.tr.setNodeMarkup(pos, null, {
                ...(current?.attrs || {}),
                src: json.url,
                height,
                width,
                file: null,
              }),
            );
            img.src = json.url;
            img.style.width = `${width}px`;
            img.style.height = `${height}px`;
            uploadProgress.remove();
          });
      }

      return {
        dom: figure,
        update(updatedNode, decorations, innerDecorations) {
          if (updatedNode.type.name !== 'image') return false;
          const newAlt = updatedNode.attrs.alt || '';
          if ((caption.textContent || '') !== newAlt && document.activeElement !== caption) {
            caption.textContent = newAlt;
          }
          img.alt = newAlt;
          if (updatedNode.attrs.src && img.src !== updatedNode.attrs.src) {
            img.src = updatedNode.attrs.src;
          }
          updateCaptionVisibility();
          return resizable.update(updatedNode, decorations, innerDecorations);
        },
        selectNode() {
          figure.classList.add('ProseMirror-selectednode');
          isSelected = true;
          updateCaptionVisibility();
        },
        deselectNode() {
          figure.classList.remove('ProseMirror-selectednode');
          isSelected = false;
          updateCaptionVisibility();
        },
        stopEvent(event) {
          // Let the figcaption handle its own DOM events.
          return caption.contains(event.target as Node);
        },
        ignoreMutation(mutation) {
          // ProseMirror should not try to reconcile contenteditable changes
          // inside the figcaption — we sync `alt` ourselves.
          return caption.contains(mutation.target as Node);
        },
        destroy() {
          resizable.destroy();
        },
      };
    };
  },
});

export default Image;
